import React, { useEffect, useMemo, useState } from 'react';

import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';

import { buildScopeRows } from '../scope';

import AgentConfigScopeEditor from './AgentConfigScopeEditor';
import AgentConfigScopeTable from './AgentConfigScopeTable';
import copies from './copies';
import type { AiExpertInfo, AiExpertScopeConfigurationInfo, ConductorConfigurationModel, ScopeRow } from './types';

const AgentConfigurationTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const baseUrl = `/sysconfig/api/v1/organizations/${organization.id}/conductor_configuration`;
  const expertsBaseUrl = `/sysconfig/api/v1/organizations/${organization.id}/ai_experts`;

  // Every (sub-org, LOB) scope the organization supports, listed whether or not it has ever
  // been configured: there is no create gesture, and the first save is what creates the row.
  const rows = useMemo(() => buildScopeRows(organization), [organization]);
  const [editScope, setEditScope] = useState<ScopeRow | null>(null);

  // Close any open editor when the sysconfig target org changes: the editor holds the previous
  // org's scope row, and a save would PATCH it against the newly selected org.
  useEffect(() => {
    setEditScope(null);
  }, [organization.id]);

  // One request for the whole table rather than one per row; a scope missing from the response
  // has no configuration, which the table renders as off.
  const { data, isLoading, isError, reloadData } = useDataFetcher<ConductorConfigurationModel[]>(`${baseUrl}/scopes`);

  // The Experts column counts the scope's experts against the ones this screen offers, so both
  // sides of that count are read once for the whole table -- the scopes read with no query params
  // answers every scope's rows in one request.
  const {
    data: experts,
    isLoading: isLoadingExperts,
    isError: isErrorExperts,
  } = useDataFetcher<AiExpertInfo[]>(expertsBaseUrl);
  const {
    data: expertScopes,
    isLoading: isLoadingExpertScopes,
    isError: isErrorExpertScopes,
    reloadData: reloadExpertScopes,
  } = useDataFetcher<AiExpertScopeConfigurationInfo[]>(`${expertsBaseUrl}/scopes`);

  // This container stays mounted while the editor is shown, so neither fetch re-runs on its own.
  // Without these refetches a column keeps the value it was rendered with, and a switch flipped in
  // the editor only shows up once the tab is remounted -- which is as true of the Experts count as
  // it is of the Conductor chip.
  const handleBack = () => {
    setEditScope(null);
    reloadData();
    reloadExpertScopes();
  };

  if (editScope) {
    return <AgentConfigScopeEditor organizationId={organization.id} scope={editScope} onBack={handleBack} />;
  }

  // One gate for the whole table: both columns are read from these three requests, and rendering
  // the table before the expert reads land would show every scope as running none of its experts.
  const isTableLoading = isLoading || isLoadingExperts || isLoadingExpertScopes;
  const isTableError = isError || isErrorExperts || isErrorExpertScopes;

  return (
    <div className="flex min-h-[50vh] flex-col gap-16 bg-white p-24">
      <div className="flex flex-col gap-4">
        <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD}>
          {copies.tabTitle}
        </Text>
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {copies.tabSubtitle}
        </Text>
      </div>
      {isTableLoading || isTableError ? (
        <SkeletonTable rowsCount={5} columnsCount={5} isError={isTableError} />
      ) : (
        <AgentConfigScopeTable
          rows={rows}
          configurations={data ?? []}
          experts={experts ?? []}
          expertScopes={expertScopes ?? []}
          onConfigure={setEditScope}
        />
      )}
    </div>
  );
};

export default AgentConfigurationTab;
